// T735 follow-up (2026-09-13) — the single owner of the brand's public social profiles.
//
// ── Why this module exists ────────────────────────────────────────────────────
//
// T735 gave the footer two real anchors and declared their URLs in `Footer.tsx`.
// It did not notice that `lib/seo/jsonld.ts` had declared the SAME two profiles
// since 2026-06-15, for JSON-LD `sameAs`. Two declarations of one fact, and they
// had already drifted before anyone read them together:
//
//   footer    https://www.instagram.com/gala.thefittclub/   https://www.youtube.com/@…
//   sameAs    https://www.instagram.com/gala.thefittclub    https://youtube.com/@…
//
// Nothing was broken — all four resolve to the same two profiles — and that is
// precisely why it could have sat there indefinitely. The next platform she adds
// would have been added to one of the two, and which one is a coin flip.
//
// ── Why it is neither of the two host modules ─────────────────────────────────
//
// `config/hosts.ts` (T424) owns OUR hosts. `config/external-origins.ts` (T653)
// owns third-party origins the app FETCHES from, and derives the CSP from them —
// the app never fetches Instagram, and a social platform landing in that map
// would suggest it needs a CSP entry it must not have. These are a third thing:
// origins the product PUBLISHES a link to.
//
// `config/contact.ts` (T597) is the precedent — the single owner of the address a
// woman is told to write to, likewise published rather than fetched, and likewise
// not one of the host gates' owners.

/**
 * The brand's public profiles, keyed by platform. Founder-supplied 2026-09-13;
 * these exact strings, trailing slash included, are what she gave.
 *
 * Keyed rather than a bare list because the footer needs to reach ONE platform to
 * build one `href`, while JSON-LD needs all of them flat. Deriving the list from
 * the map (below) is what makes a third platform reach both surfaces by adding a
 * single line here.
 */
export const SOCIAL_PROFILES = {
  instagram: "https://www.instagram.com/gala.thefittclub/",
  youtube: "https://www.youtube.com/@gala.thefittclub",
  linkedin: "https://www.linkedin.com/company/thefittclub/",
  /** Founder-supplied 2026-11-02 (her screenshot of the page's own link). The reviews tab is `/reviews` on this path. */
  facebook: "https://www.facebook.com/gala.thefittclub",
} as const;

/**
 * The same profiles as a flat list, for JSON-LD `sameAs` — "this entity is also
 * found at these pages".
 *
 * DERIVED, never a second literal: a hand-kept copy is exactly the drift recorded
 * at the top of this file.
 */
export const SOCIAL_PROFILE_URLS: readonly string[] = Object.values(SOCIAL_PROFILES);
